import type {
    AvailableFieldBlockInfo,
    AvailableFilterBlockInfo,
    BlockRuntimeContext,
    ContainerRuntimeState,
    FormContainerBlockAbstract,
    PageItem,
    PageStackTree,
    PageStateWithValue,
    PageVariableMappingRecord,
    ViewBlockAbstract,
    ViewRuntimeState
} from '@lighthouse/core'
import { type BlockAbstract, BlockType, PAGE_STACK_DISPLAY_TYPE, type PageAbstract } from '@lighthouse/core'
import {
    buildBlockUniqueKey,
    buildPageBlockContextIndex,
    findRecordInPool,
    getPageWithMergedBreakPoint,
    transformOriginBlock
} from '@lighthouse/shared'
import equal from 'fast-deep-equal'
import { type Draft } from 'immer'
import { atom } from 'jotai'
import { atomFamily, selectAtom } from 'jotai/utils'
import { atomWithImmer } from 'jotai-immer'
import { clone, omit } from 'rambda'

import { currentBreakPointAtom, languageAtom, mainBreakPointAtom, pageSyncComponentsAtom } from '../application/state'
import { dataSourceAtomFamily, recordPoolAtom } from '../dataSource/state'
import { applyDraftPayload } from '../utils/applyDraftPayload'
import { equalPageStack } from '../utils/equalPageStack'
import { findByPageByTree, pageTreeToPageList } from '../utils/page'
import type { ContainerDesignState, PageInternalState } from './types'
import { AsideType } from './types'
import { getNode } from './utils'

export const pageTreeListAtom = atomWithImmer<PageItem[]>([])

export const pageListAtom = atom<(PageAbstract & { path?: string })[]>(get => pageTreeToPageList(get(pageTreeListAtom), []))

export const languagePageListAtom = atom(get => {
    const list = get(pageListAtom)
    const language = get(languageAtom)
    return list.filter(page => page.language === language)
})

export const homePageAtom = atom(get => {
    const list = get(languagePageListAtom)
    const language = get(languageAtom)
    return list.find(page => page.isHome && page.language === language)
})

export const pageAtomFamily = atomFamily(
    (pageID: string) =>
        atom(
            get => {
                const page = get(pageListAtom)?.find(item => item.id === pageID)
                if (!page) {
                    return
                }
                const mainBreakPoint = get(mainBreakPointAtom)
                const breakPointId = get(currentBreakPointAtom)?.id || mainBreakPoint
                return getPageWithMergedBreakPoint(breakPointId, mainBreakPoint, page)
            },
            (_, set, payload: PageAbstract | null | ((draft: Draft<PageAbstract | null>) => void)) =>
                set(pageTreeListAtom, draft => {
                    const page = findByPageByTree(draft)(pageID) ?? null
                    if (page?.type === 'COMMON' && payload) {
                        applyDraftPayload(page, payload)
                    }
                })
        ),
    equal
)

/** 所有block */
export const blocksAtom = atomWithImmer<Partial<Record<string, BlockAbstract[]>>>({})

/** 页面block-断点属性合并 */
export const pageBlocksAtom = atomFamily((pageId: string) => {
    return atom(get => {
        const originBlocks = get(selectAtom(blocksAtom, s => s[pageId], equal))
        if (!originBlocks) {
            return []
        }
        const mainBreakPoint = get(mainBreakPointAtom)
        const breakPointId = get(currentBreakPointAtom)?.id || mainBreakPoint
        return transformOriginBlock(originBlocks, breakPointId, mainBreakPoint)
    })
}, equal)

export const pageStackAtom = atomWithImmer<PageStackTree | null>(null)
export const pageStackHistoriesAtom = atomWithImmer<string[]>([])

/** 面包屑的pageStack */
export const pageStackBreadcrumbAtomFamily = atomFamily((stackId: string) =>
    atom(get => {
        const tree = get(pageStackAtom)

        if (!tree) {
            return []
        }

        function reverseRecursion(tree: PageStackTree): PageStackTree[] {
            if (tree.stackId === stackId) {
                return [tree]
            }
            if (tree.children) {
                for (const stack of tree.children) {
                    const res = reverseRecursion(stack)
                    // 有值说明找到子节点了，直接一路往上返回
                    if (res.length > 0) {
                        return [tree, ...res]
                    }
                }
            }

            return []
        }

        return reverseRecursion(tree)
    })
)

export const pageStackAtomFamily = atomFamily(
    (stackId: string) =>
        atom(get => {
            const stackTree = get(pageStackAtom)
            return equalPageStack(stackId)(stackTree)
        }),
    equal
)

/** 弹窗层的pageStack */
export const pageStackLayersAtom = atom(get => {
    const tree = get(pageStackAtom)
    const indexes = get(pageStackHistoriesAtom)
    const stackId = indexes[indexes.length - 1]

    if (!tree || !tree.children) {
        return []
    }

    function reverseRecursion(children: PageStackTree[]): PageStackTree[] {
        for (const stack of children) {
            // 找到子节点，直接返回
            if (stack.stackId === stackId) {
                return [stack]
            }

            if (stack.children) {
                const res = reverseRecursion(stack.children)
                // 有值说明找到子节点了，直接一路往上返回
                if (res.length > 0) {
                    // 如果当前栈不是页面形式的，也加入到弹窗列表中
                    if (stack.pageStackDisplayType !== PAGE_STACK_DISPLAY_TYPE.page) {
                        return [omit('children', stack), ...res]
                    }
                    return res
                }
            }
        }

        return []
    }

    return reverseRecursion(tree.children)
}, equal)

/** 最后的pageStack */
export const lastPageOfStackAtom = atom<PageStackTree | undefined>(get => {
    const stackTree = get(pageStackAtom)
    if (!stackTree) {
        return
    }
    const indexes = get(pageStackHistoriesAtom)
    if (indexes.length === 0) {
        return
    }
    const stackId = indexes[indexes.length - 1]

    return getNode(stackId)(stackTree)
})

/** 最后的页面形式的pageStack */
export const lastPageTypeOfStackAtom = atom(get => {
    const stackTree = get(pageStackAtom)
    if (!stackTree) {
        return
    }
    const indexes = get(pageStackHistoriesAtom)
    const stackId = indexes[indexes.length - 1]
    if (!stackId) {
        return
    }

    let recentPageTypeParentStack: PageStackTree | undefined
    function getPageTypeStackId(tree: PageStackTree): boolean {
        if (!tree.children) {
            return false
        }

        for (const node of tree.children) {
            if (node.stackId === stackId) {
                if (node.pageStackDisplayType === PAGE_STACK_DISPLAY_TYPE.page && !recentPageTypeParentStack) {
                    recentPageTypeParentStack = omit('children', node)
                }
                return true
            }

            const res = getPageTypeStackId(node)
            if (res) {
                if (node.pageStackDisplayType === PAGE_STACK_DISPLAY_TYPE.page && !recentPageTypeParentStack) {
                    recentPageTypeParentStack = omit('children', node)
                }
                return res
            }
        }

        return false
    }

    getPageTypeStackId(stackTree)

    return recentPageTypeParentStack ?? omit<PageStackTree, PageStackTree>('children', stackTree)
})

/** 外部添加拖拽时的node */
export const outsideDraggingNode = atomWithImmer<BlockAbstract | null>(null)

/** 容器设计配置的交互状态 pageId/containerId -> ContainerDesignState */
export const containerDesignStateAtom = atomWithImmer<Record<string, ContainerDesignState | undefined>>({})

/** block高亮的状态 */
export const blockHighlightAtom = atomWithImmer<string[]>([])

/** 创建block时loading */
export const createNodeLoadingAtom = atom(false)

/**
 * block创建时的列表, 用于前端创建某些视图block，
 * 后端的创建接口还未完成时，前端的视图已经渲染并发出获取视图请求报错的问题
 */
export const blockCreatingListAtom = atom<string[]>([])

/**
 * dataDrawer进行增删改后, 需要重新获取选中的视图，图表block数据
 */
export const blockUpdateListAtom = atom<string[]>([])

export const pageVariableSchemasFamily = atomFamily(
    (pageId: string) => atom(get => get(pageAtomFamily(pageId))?.parameterAndVariable?.variables),
    equal
)
export const pagePathParameterFamily = atomFamily(
    (pageId: string) => atom(get => get(pageAtomFamily(pageId))?.parameterAndVariable?.pathParameter),
    equal
)
export const pageQueryParameterFamily = atomFamily(
    (pageId: string) => atom(get => get(pageAtomFamily(pageId))?.parameterAndVariable?.queryParameter),
    equal
)

export const pageStatesParameterFamily = atomFamily(
    (pageId: string) => atom(get => get(pageAtomFamily(pageId))?.parameterAndVariable?.states),
    equal
)

export const pageVariableRecordMapAtom = atomWithImmer<PageVariableMappingRecord>({})

export const pageVariableRecordFamily = atomFamily(
    ({ stackId, variableId }: { stackId: string; variableId: string }) =>
        atom(get => {
            const dependIds = get(pageVariableRecordMapAtom)[stackId]?.[variableId]
            if (!dependIds) {
                return
            }
            const { appId, envId, dsId, recordId } = dependIds
            const ds = get(dataSourceAtomFamily({ appId, envId, dsId }))
            if (!ds) {
                return
            }
            const record = findRecordInPool(get(recordPoolAtom), dsId, recordId)
            if (!record) {
                return
            }
            return { ds, record }
        }),
    (a, b) => a.stackId === b.stackId && a.variableId === b.variableId
)

export const pageVariableValueFamily = atomFamily(
    ({ stackId, pageId, id, itemId }: { stackId: string; pageId: string; id: string; itemId: string }) =>
        atom(get => {
            const pageVariableRecord = get(pageVariableRecordFamily({ stackId, variableId: id }))
            if (!pageVariableRecord) {
                return
            }

            const { ds, record } = pageVariableRecord
            const pageVariableSchema = get(pageVariableSchemasFamily(pageId))

            const fieldId = pageVariableSchema?.find(item => item.id === id)?.items.find(item => item.id === itemId)?.fieldId
            if (!fieldId) {
                return
            }

            return { ds, record, fieldId }
        }),
    equal
)

// ============== 独立的运行时状态 Atom ==============

/**
 * 页面内部状态 - 按 stackId 存储 (builder only)
 * 包含编辑器的选中状态、侧边栏类型等
 */
export const pageInternalStateAtomFamily = atomFamily(
    (stackId: string) =>
        atomWithImmer<PageInternalState>({
            asideType: AsideType.PAGE,
            selectedNodes: []
        }),
    (a, b) => a === b
)

/**
 * 页面状态变量 - 按 stackId 存储
 */
export const pageStateAtomFamily = atomFamily(
    (stackId: string) => atomWithImmer<PageStateWithValue[]>([]),
    (a, b) => a === b
)

/**
 * Container 运行时状态 - 按 stackId + containerId 存储
 */
export const containerRuntimeStateAtomFamily = atomFamily(
    ({ stackId, containerId }: { stackId: string; containerId: string }) => atomWithImmer<ContainerRuntimeState | null>(null),
    (a, b) => a.stackId === b.stackId && a.containerId === b.containerId
)

/**
 * View 选择状态 - 按 stackId 存储
 * 每个页面栈（包括弹窗页面）可以独立存在选中记录
 */
export const viewRuntimeStateAtomFamily = atomFamily(
    (stackId: string) => atomWithImmer<ViewRuntimeState | null>(null),
    (a, b) => a === b
)

/**
 * 页面 block 上下文索引
 * 一次 O(n) 构建，所有消费方共享，避免重复递归查找
 * 依赖：pageBlocks、syncComponents、各 container 的运行时状态
 */
export const pageBlockContextIndexAtom = atom(get => {
    const lastPage = get(lastPageOfStackAtom)
    if (!lastPage) {
        return null
    }

    const { pageId } = lastPage
    const blocks = get(pageBlocksAtom(pageId)) || []
    const syncComponents = get(pageSyncComponentsAtom)

    return buildPageBlockContextIndex({
        blocks,
        syncComponents: clone(syncComponents)
    })
})

/**
 * 当前选中 block 的运行时上下文
 * 从 pageBlockContextIndexAtom 直接查表，O(1) 复杂度
 */
export const selectedBlockContextAtom = atom<BlockRuntimeContext | null>(get => {
    const lastPage = get(lastPageOfStackAtom)
    if (!lastPage) return null

    const { stackId } = lastPage
    const internalState = get(pageInternalStateAtomFamily(stackId))
    const selectNode = internalState?.selectedNodes?.[0]
    if (!selectNode?.id) return null

    const index = get(pageBlockContextIndexAtom)
    if (!index) return null

    const uniqueKey = buildBlockUniqueKey(selectNode.id, selectNode.scope)
    return index.contextMap.get(uniqueKey) ?? null
})

/**
 * 当前选中 block 所属的 CustomView（最近祖先）
 */
export const selectedBlockCustomViewAtom = atom<ViewBlockAbstract | undefined>(get => {
    const context = get(selectedBlockContextAtom)
    return context?.customView
})

/**
 * 当前选中 block 所属的 FormContainer（最近祖先）
 */
export const selectedBlockFormContainerAtom = atom<FormContainerBlockAbstract | undefined>(get => {
    const context = get(selectedBlockContextAtom)
    return context?.formContainer
})

/** 从根到当前选中 block 的路径（与 getBlockPath 语义一致），基于 index 的 parentMap 组装，O(depth) */
export const selectedBlockPathAtom = atom<Array<{ block: BlockAbstract; scope?: string }>>(get => {
    const context = get(selectedBlockContextAtom)
    const index = get(pageBlockContextIndexAtom)
    if (!context || !index) return []

    const path: Array<{ block: BlockAbstract; scope?: string }> = []
    let uniqueKey: string | undefined = context.uniqueKey
    while (uniqueKey) {
        const ctx = index.contextMap.get(uniqueKey)
        if (ctx) path.push({ block: ctx.block, scope: ctx.scope })
        uniqueKey = index.parentMap.get(uniqueKey)
    }
    path.reverse()
    return path
})

/**
 * 当前选中 block 可用的 field/onOff block 列表
 * 过滤规则：只返回与选中 block 同一 customView 下的，或不在 customView 中的；多面板时只返回当前面板
 */
export const availableFieldBlocksAtom = atom(get => {
    const index = get(pageBlockContextIndexAtom)
    if (!index) return [] as AvailableFieldBlockInfo[]

    const lastPage = get(lastPageOfStackAtom)
    const stackId = lastPage?.stackId ?? ''
    const selectedCustomView = get(selectedBlockCustomViewAtom)
    const selectedViewId = selectedCustomView?.id ?? ''

    const result: AvailableFieldBlockInfo[] = []
    for (const [, context] of index.contextMap) {
        const { block, customView, panelId, container, scope } = context
        if (block.type !== BlockType.field && block.type !== BlockType.onOff) {
            continue
        }
        if (customView?.id && customView.id !== selectedViewId) {
            continue
        }
        if (panelId != null && container) {
            const containerId = scope ? `${scope}@${container.id}` : container.id
            const currentView = get(containerRuntimeStateAtomFamily({ stackId, containerId }))?.currentView
            if (currentView !== panelId) continue
        }
        result.push(context as AvailableFieldBlockInfo)
    }
    return result
})

/**
 * 当前选中 block 可用的 filter block 列表
 * 过滤规则：只返回与选中 block 同一 customView 下的，或不在 customView 中的；多面板时只返回当前面板
 */
export const availableFilterBlocksAtom = atom(get => {
    const index = get(pageBlockContextIndexAtom)
    if (!index) return [] as AvailableFilterBlockInfo[]

    const lastPage = get(lastPageOfStackAtom)
    const stackId = lastPage?.stackId ?? ''
    const selectedCustomView = get(selectedBlockCustomViewAtom)
    const selectedViewId = selectedCustomView?.id ?? ''

    const result: AvailableFilterBlockInfo[] = []
    for (const [, context] of index.contextMap) {
        const { block, customView, panelId, container, scope } = context
        if (block.type !== BlockType.filter) {
            continue
        }
        if (customView?.id && customView.id !== selectedViewId) {
            continue
        }
        if (panelId != null && container) {
            const containerId = scope ? `${scope}@${container.id}` : container.id
            const currentView = get(containerRuntimeStateAtomFamily({ stackId, containerId }))?.currentView
            if (currentView !== panelId) continue
        }
        result.push(context as AvailableFilterBlockInfo)
    }
    return result
})
